/**
 * The dashboard landing — list of all submissions with filter controls.
 *
 * Filters: status, inspector, date range. We refetch on every filter change
 * with a small debounce window so typing a date doesn't spam the backend.
 *
 * Laid out with the Jobs Overview furniture — `Kpi` tiles, an `overview-band`
 * filter row, an `overview-jobs-row` grid — so the two list screens read as one
 * product rather than two eras of it. The tiles double as filters here for the
 * same reason they do on Jobs: the number you just read is the set you want.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clearSession, getSession } from '../session.js';
import type {
  AdminReportFilter,
  AdminReportStatus,
  AdminReportSummary,
  DropboxSyncImportResult,
  FieldReadinessState,
} from '@cer-autofill/shared';
import {
  KNOWN_INSPECTORS,
  fieldReadiness,
  uploadDeadline,
  uploadDeadlineLabel,
  UPLOAD_DEADLINE_DAYS,
  UPLOAD_WARN_AFTER_DAYS,
  type UploadUrgency,
} from '@cer-autofill/shared';
import { fetchDropboxSyncCandidates, fetchReports, importDropboxReport } from '../api.js';
import { messageForLegacyImportFailure, SyncDropboxButton } from '../lib/legacy-dropbox-sync.js';
import { formatDateTime } from '../format.js';
import { StatusPill, ClauseCounts, ReadinessPill } from '../components/StatusPill.js';
import { Kpi } from '../components/Kpi.js';
import { FilterSelect } from '../components/FilterSelect.js';
import { percentage } from '../components/trend.js';

/** QA outcomes. The lifecycle is the job's, and shows in the upload clock. */
const STATUS_OPTIONS: [string, string][] = [
  ['pending', 'Awaiting QA'],
  ['approved', 'QA passed / approved for portal'],
  ['information-required', 'Additional information required'],
  ['revisit-required', 'Return to site'],
];

const READINESS_OPTIONS: [string, string][] = [
  ['needs-work', 'Needs work'],
  ['complete', 'Ready for QA'],
];

/**
 * The upload clock, as a filter. `attention` is the one a reviewer actually
 * wants most mornings — everything the 10-day obligation is closing on,
 * overdue first — so it leads.
 */
const DEADLINE_OPTIONS: [string, string][] = [
  ['attention', `Needs attention (${UPLOAD_WARN_AFTER_DAYS}d+ since inspection)`],
  ['overdue', `Overdue (${UPLOAD_DEADLINE_DAYS}d+)`],
  ['due-soon', 'Due soon'],
  ['ok', 'In hand'],
  ['unknown', 'No inspection date'],
];

interface FilterState {
  status: '' | AdminReportStatus;
  inspectorId: string;
  fromDate: string;
  toDate: string;
  /**
   * Applied client-side, unlike the others. Readiness is derived from the
   * submission body rather than stored alongside the status index, so there is
   * nothing for the store to filter on — and the list already caps at 200 rows.
   */
  readiness: '' | FieldReadinessState;
  /** Free text over job id, homeowner and site address. */
  search: string;
  /** One of DEADLINE_OPTIONS, or '' for any. */
  deadline: string;
}

const INITIAL_FILTER: FilterState = {
  status: '',
  inspectorId: '',
  fromDate: '',
  toDate: '',
  readiness: '',
  search: '',
  deadline: '',
};

/**
 * One row of "Sync Dropbox" output. The server never says `error` — that arm
 * records a request that failed outright (network, 5xx) so one bad job never
 * hides the outcomes of the rest.
 */
type SyncRowOutcome = DropboxSyncImportResult['outcome'] | 'error';
interface SyncRow {
  jobId: string;
  outcome: SyncRowOutcome;
  detail: string;
}

interface SyncState {
  phase: 'idle' | 'running' | 'done';
  total: number;
  /** Job currently being imported, while running. */
  current?: string;
  rows: SyncRow[];
  /** Fatal error before the loop started (candidates fetch failed). */
  error?: string;
}

const SYNC_IDLE: SyncState = { phase: 'idle', total: 0, rows: [] };

function syncRowFromResult(result: DropboxSyncImportResult): SyncRow {
  if (result.outcome === 'imported') {
    const s = result.summary;
    const detail = s
      ? `${s.clauses} clauses (${s.nonCompliant} non-compliant), ${s.photos} photos${
          s.checklistPdf ? ` — from "${s.checklistPdf}"` : ''
        }`
      : 'imported';
    return { jobId: result.jobId, outcome: 'imported', detail };
  }
  return { jobId: result.jobId, outcome: result.outcome, detail: result.reason ?? '' };
}

export function ReportsListScreen() {
  const [filter, setFilter] = useState<FilterState>(INITIAL_FILTER);
  const [rows, setRows] = useState<AdminReportSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState<SyncState>(SYNC_IDLE);
  // Bumped when a sync lands reports so the list refetches without touching filters.
  const [refreshTick, setRefreshTick] = useState(0);

  // Debounce filter changes — date inputs especially fire many onChange calls.
  const debouncedFilter = useDebounced(filter, 200);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const apiFilter: Partial<AdminReportFilter> = {};
    if (debouncedFilter.status) apiFilter.status = debouncedFilter.status;
    if (debouncedFilter.inspectorId) apiFilter.inspectorId = debouncedFilter.inspectorId;
    if (debouncedFilter.fromDate) apiFilter.fromDate = debouncedFilter.fromDate;
    if (debouncedFilter.toDate) apiFilter.toDate = debouncedFilter.toDate;
    apiFilter.limit = 200;
    void fetchReports(apiFilter)
      .then((data) => {
        if (cancelled) return;
        setRows(data);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load reports.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedFilter, refreshTick]);

  /**
   * "Sync Dropbox" — pull any legacy-process reports that aren't in the list
   * yet. One import request per job, with bounded photo transfers within it,
   * sequential so the progress line means something and the backend never
   * sees a thundering herd of Dropbox scans.
   */
  async function runDropboxSync(): Promise<void> {
    setSync({ phase: 'running', total: 0, rows: [] });
    let candidates;
    try {
      candidates = await fetchDropboxSyncCandidates();
    } catch (e: unknown) {
      setSync({
        phase: 'done',
        total: 0,
        rows: [],
        error: messageForLegacyImportFailure(
          e,
          e instanceof Error ? e.message : 'Failed to list sync candidates.',
        ),
      });
      return;
    }
    const collected: SyncRow[] = [];
    let imported = 0;
    for (const candidate of candidates) {
      setSync({
        phase: 'running',
        total: candidates.length,
        current: candidate.jobId,
        rows: [...collected],
      });
      try {
        const result = await importDropboxReport(candidate.jobId);
        if (result.outcome === 'imported') imported++;
        collected.push(syncRowFromResult(result));
      } catch (e: unknown) {
        collected.push({
          jobId: candidate.jobId,
          outcome: 'error',
          detail: messageForLegacyImportFailure(
            e,
            e instanceof Error ? e.message : 'Import request failed.',
          ),
        });
      }
    }
    setSync({ phase: 'done', total: candidates.length, rows: collected });
    if (imported > 0) setRefreshTick((t) => t + 1);
  }

  const updateFilter = (patch: Partial<FilterState>) => {
    setFilter((prev) => ({ ...prev, ...patch }));
  };

  const navigate = useNavigate();
  const session = getSession();

  // Readiness per row, derived once — the pills, the metric and the filter all
  // read the same verdict. The live count is how many submit-time lines are
  // still open; the original length stays on the row for stats.
  const readinessByRow = useMemo(() => {
    const map = new Map<AdminReportSummary, ReturnType<typeof fieldReadiness>>();
    for (const row of rows ?? []) {
      map.set(row, fieldReadiness(row.outstandingNowCount ?? row.outstandingAtSubmitCount));
    }
    return map;
  }, [rows]);

  /**
   * The upload clock per row, derived once. Ten days from the inspection
   * date to the portal; the queue warns from seven. Keyed by row so the pills, the filter
   * and the KPI tile all read one verdict.
   */
  const deadlineByRow = useMemo(() => {
    const now = new Date();
    const map = new Map<AdminReportSummary, ReturnType<typeof uploadDeadline>>();
    for (const row of rows ?? []) {
      map.set(
        row,
        uploadDeadline({
          inspectionDate: row.inspectionDate,
          ...(row.jobStatus !== undefined ? { jobStatus: row.jobStatus } : {}),
          now,
        }),
      );
    }
    return map;
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!rows) return null;
    const needle = debouncedFilter.search.trim().toLowerCase();
    return rows.filter((row) => {
      if (
        debouncedFilter.readiness &&
        readinessByRow.get(row)?.state !== debouncedFilter.readiness
      ) {
        return false;
      }
      const urgency = deadlineByRow.get(row)?.urgency;
      if (debouncedFilter.deadline === 'attention') {
        if (urgency !== 'overdue' && urgency !== 'due-soon') return false;
      } else if (debouncedFilter.deadline && urgency !== debouncedFilter.deadline) {
        return false;
      }
      if (needle) {
        // Job id, homeowner and address — the three things someone has in
        // hand when they come looking for a specific report.
        const haystack =
          `${row.jobId} ${row.homeownerName ?? ''} ${row.siteAddress ?? ''}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [
    rows,
    readinessByRow,
    deadlineByRow,
    debouncedFilter.readiness,
    debouncedFilter.deadline,
    debouncedFilter.search,
  ]);

  /** Rows the 10-day obligation is closing on, worst first. */
  const attention = useMemo(() => {
    const ranked = (rows ?? []).filter((row) => {
      const u = deadlineByRow.get(row)?.urgency;
      return u === 'overdue' || u === 'due-soon';
    });
    return ranked.sort(
      (a, b) =>
        (deadlineByRow.get(a)?.daysRemaining ?? 0) - (deadlineByRow.get(b)?.daysRemaining ?? 0),
    );
  }, [rows, deadlineByRow]);

  // The tiles describe the fetched population (`rows`), not the readiness
  // drill-down (`visibleRows`) — otherwise clicking "Needs Work" makes its own
  // ring read 100% of a set it just defined. The grid narrows; the tiles hold
  // still, and the band heading says how many of the fetched set survived.
  const fetched = rows ?? [];
  // Outstanding = QA has not passed it. Whether it then reached CER is the
  // job's business and shows in the upload clock, not here.
  const outstanding = fetched.filter((row) => row.status !== 'approved');
  const summary = {
    pv: outstanding.filter((row) => row.jobId.startsWith('PVD')).length,
    bty: outstanding.filter((row) => row.jobId.startsWith('BTY')).length,
    information: outstanding.filter((row) => row.status === 'information-required').length,
    revisit: outstanding.filter((row) => row.status === 'revisit-required').length,
    // Counted over every fetched row, not just the not-yet-approved ones: an
    // approved report that arrived short is exactly what a reviewer wants to
    // see, and it would otherwise be invisible here.
    needsWork: fetched.filter((row) => readinessByRow.get(row)?.state === 'needs-work').length,
  };

  function signOut(): void {
    clearSession();
    void navigate('/login', { replace: true });
  }

  /** Apply a filter from a KPI tile and drop the reader at the grid, as Jobs does. */
  function applyFromKpi(patch: Partial<FilterState>): void {
    updateFilter(patch);
    document.getElementById('reports-grid')?.scrollIntoView({ behavior: 'smooth' });
  }

  const listed = rows?.length;

  return (
    <main className="screen reports-overview">
      <div className="screen__header">
        <div>
          <h2>Reports</h2>
          <p className="subtitle">Review inspections and prepare reports for upload</p>
        </div>
        <div className="identity-bar">
          {session && (
            <>
              <span className="identity-bar__name">Signed in as {session.name}</span>
              <button type="button" className="link-button" onClick={signOut}>
                Switch user
              </button>
            </>
          )}
        </div>
      </div>

      {error && <p className="error">Error loading reports: {error}</p>}

      <section className="overview-kpis" aria-label="Report KPIs">
        <Kpi
          label="Outstanding"
          value={rows ? outstanding.length : undefined}
          unit="Reports"
          tone="blue"
          icon="clipboard"
          percent={percentage(outstanding.length, listed)}
          percentCaption="of loaded reports"
        />
        <Kpi
          label="Upload Clock"
          value={rows ? attention.length : undefined}
          unit="Reports"
          tone={
            attention.some((r) => deadlineByRow.get(r)?.urgency === 'overdue') ? 'red' : 'amber'
          }
          icon="alert"
          percent={percentage(attention.length, listed)}
          percentCaption="of loaded reports"
          footnote={
            rows
              ? attention.length === 0
                ? { text: `All inside ${UPLOAD_WARN_AFTER_DAYS} days`, tone: 'good' }
                : {
                    text: `${attention.filter((r) => deadlineByRow.get(r)?.urgency === 'overdue').length} past ${UPLOAD_DEADLINE_DAYS} days`,
                    tone: 'risk',
                  }
              : undefined
          }
          onClick={() => applyFromKpi({ deadline: 'attention' })}
        />
        <Kpi
          label="Needs Work"
          value={rows ? summary.needsWork : undefined}
          unit="Reports"
          tone="amber"
          icon="alert"
          percent={percentage(summary.needsWork, listed)}
          percentCaption="of loaded reports"
          footnote={
            rows && !summary.needsWork
              ? { text: 'No outstanding field items', tone: 'good' }
              : undefined
          }
          onClick={() => applyFromKpi({ readiness: 'needs-work' })}
        />
        <Kpi
          label="PV Reports"
          value={rows ? summary.pv : undefined}
          unit="Reports"
          tone="violet"
          icon="clipboard"
        />
        <Kpi
          label="BTY Reports"
          value={rows ? summary.bty : undefined}
          unit="Reports"
          tone="green"
          icon="clipboard"
        />
        <Kpi
          label="Additional Information"
          value={rows ? summary.information : undefined}
          unit="Reports"
          tone="amber"
          icon="alert-circle"
          footnote={
            rows && !summary.information ? { text: 'None waiting', tone: 'good' } : undefined
          }
          onClick={() => applyFromKpi({ status: 'information-required' })}
        />
        <Kpi
          label="Return To Site"
          value={rows ? summary.revisit : undefined}
          unit="Reports"
          tone="red"
          icon="flag"
          footnote={
            rows
              ? summary.revisit
                ? { text: 'Needs attention', tone: 'risk' }
                : { text: 'None outstanding', tone: 'good' }
              : undefined
          }
          onClick={() => applyFromKpi({ status: 'revisit-required' })}
        />
      </section>

      <section className="overview-band overview-filters" aria-label="Report filters">
        <div className="filter-grid filter-grid--reports">
          <label>
            <span>Search</span>
            <input
              type="search"
              value={filter.search}
              placeholder="Job, homeowner or address"
              onChange={(e) => updateFilter({ search: e.target.value })}
            />
          </label>
          <FilterSelect
            label="Upload clock"
            value={filter.deadline}
            onChange={(value) => updateFilter({ deadline: value })}
            options={DEADLINE_OPTIONS}
            allLabel="Any deadline"
          />
          <FilterSelect
            label="QA outcome"
            value={filter.status}
            onChange={(value) => updateFilter({ status: value as FilterState['status'] })}
            options={STATUS_OPTIONS}
            allLabel="All statuses"
          />
          <FilterSelect
            label="From field"
            value={filter.readiness}
            onChange={(value) => updateFilter({ readiness: value as FilterState['readiness'] })}
            options={READINESS_OPTIONS}
            allLabel="Any readiness"
          />
          <FilterSelect
            label="Inspector"
            value={filter.inspectorId}
            onChange={(value) => updateFilter({ inspectorId: value })}
            options={KNOWN_INSPECTORS.map((name) => [name, name])}
            allLabel="All inspectors"
          />
          <label>
            <span>From</span>
            <input
              type="date"
              value={filter.fromDate}
              onChange={(e) => updateFilter({ fromDate: e.target.value })}
            />
          </label>
          <label>
            <span>To</span>
            <input
              type="date"
              value={filter.toDate}
              onChange={(e) => updateFilter({ toDate: e.target.value })}
            />
          </label>
        </div>
        <div className="filter-actions">
          <SyncDropboxButton
            enabled={session?.dropboxLegacyImportEnabled}
            running={sync.phase === 'running'}
            onClick={() => void runDropboxSync()}
          />
          <button type="button" className="ghost-btn" onClick={() => setFilter(INITIAL_FILTER)}>
            Clear
          </button>
        </div>
      </section>

      {sync.phase !== 'idle' && (
        <section className="overview-band" aria-label="Dropbox sync">
          <div className="band-heading">
            <h3>Sync Dropbox</h3>
            {sync.phase === 'running' ? (
              <span>
                {sync.total === 0
                  ? 'Finding booked jobs without reports…'
                  : `Importing ${Math.min(sync.rows.length + 1, sync.total)} of ${sync.total}${
                      sync.current ? ` — ${sync.current}` : ''
                    }`}
              </span>
            ) : (
              <span>
                {sync.error
                  ? 'Sync failed'
                  : sync.total === 0
                    ? 'Nothing to sync — every booked job already has a report.'
                    : `${sync.rows.filter((r) => r.outcome === 'imported').length} imported, ${sync.rows.filter((r) => r.outcome === 'not-ready').length} not ready, ${sync.rows.filter((r) => r.outcome === 'skipped').length} skipped${
                        sync.rows.some((r) => r.outcome === 'error')
                          ? `, ${sync.rows.filter((r) => r.outcome === 'error').length} failed`
                          : ''
                      }`}
              </span>
            )}
            {sync.phase === 'done' && (
              <button type="button" className="link-button" onClick={() => setSync(SYNC_IDLE)}>
                Dismiss
              </button>
            )}
          </div>
          {sync.error && <p className="error">{sync.error}</p>}
          {sync.rows.length > 0 && (
            <ul className="sync-results">
              {sync.rows.map((row) => (
                <li
                  key={row.jobId}
                  className={`sync-results__row sync-results__row--${row.outcome}`}
                >
                  <span className="mono">{row.jobId}</span>
                  <span className="sync-results__outcome">
                    {row.outcome === 'imported'
                      ? 'Imported'
                      : row.outcome === 'not-ready'
                        ? 'Not ready'
                        : row.outcome === 'skipped'
                          ? 'Skipped'
                          : 'Failed'}
                  </span>
                  <span className="sync-results__detail">{row.detail}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section id="reports-grid" className="overview-band">
        <div className="band-heading">
          <h3>Reports</h3>
          <span>
            {visibleRows
              ? `${visibleRows.length} matching${
                  rows && visibleRows.length !== rows.length ? ` of ${rows.length} loaded` : ''
                }`
              : 'Loading…'}
          </span>
        </div>
        {loading && !rows ? (
          <p className="loading">Loading reports…</p>
        ) : visibleRows && visibleRows.length === 0 ? (
          <p className="loading">No reports match these filters.</p>
        ) : (
          <div role="table">
            <div className="overview-jobs-row reports-row reports-row--header" role="row">
              <div>Job</div>
              <div>From field</div>
              <div>Inspector</div>
              <div>Upload clock</div>
              <div>Submitted</div>
              <div>Approved</div>
              <div>Homeowner</div>
              <div>Clauses</div>
              <div>Photos</div>
            </div>
            {visibleRows?.map((row) => (
              <Link
                key={`${row.jobId}:${row.submittedAt}`}
                className="overview-jobs-row reports-row"
                role="row"
                to={`/reports/${encodeURIComponent(row.jobId)}/${encodeURIComponent(row.submittedAt)}`}
              >
                <div className="reports-row__job">
                  <span className="mono">{row.jobId}</span>
                  <StatusPill status={row.status} portalApprovedAt={row.portalApprovedAt} />
                  {Boolean(row.hardwareConflictCount) && (
                    <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>
                      {row.hardwareConflictCount} hardware conflict(s)
                    </span>
                  )}
                  {row.versions.length > 1 && (
                    <span
                      className="version-pill"
                      title={`${row.versions.length} versions — latest revision ${formatDateTime(row.submittedAt)}`}
                    >
                      v{row.versions.length}
                    </span>
                  )}
                </div>
                <div>
                  <ReadinessPill readiness={readinessByRow.get(row) ?? fieldReadiness(undefined)} />
                </div>
                <div>{row.inspectorId}</div>
                <div>
                  {(() => {
                    const d = deadlineByRow.get(row) ?? { urgency: 'unknown' as UploadUrgency };
                    return (
                      <span
                        className={`deadline-pill deadline-pill--${d.urgency}`}
                        title={
                          d.dueDate
                            ? `Inspected ${row.inspectionDate}; due at the portal by ${d.dueDate} (${UPLOAD_DEADLINE_DAYS} days).`
                            : 'No inspection date recorded, so the upload clock cannot be read.'
                        }
                      >
                        {uploadDeadlineLabel(d)}
                      </span>
                    );
                  })()}
                </div>
                <div>{formatDateTime(inspectorSubmittedAt(row))}</div>
                <div>
                  {row.portalApprovedAt ? (
                    formatDateTime(row.portalApprovedAt)
                  ) : (
                    <span className="muted">—</span>
                  )}
                </div>
                <div>{row.homeownerName ?? <span className="muted">—</span>}</div>
                <div>
                  <ClauseCounts counts={row.clauseCounts} />
                </div>
                <div>{row.photoCount}</div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

/** Oldest revision key: the inspector's submit, not a later supervisor edit. */
function inspectorSubmittedAt(row: {
  versions: { submittedAt: string }[];
  submittedAt: string;
}): string {
  return row.versions.reduce(
    (earliest, version) => (version.submittedAt < earliest ? version.submittedAt : earliest),
    row.submittedAt,
  );
}

/** Tiny debounce hook — no extra deps. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}
